import api from "@/utils/axios";

/*
 * GET helper for the dashboard statistics. The dashboard used to fire all its
 * statistic requests at the same time, and again on every visit; with many
 * admins connected that piles up heavy queries on the server. Here:
 *  - at most MAX_PARALLEL statistic requests run at once, the rest wait in order;
 *  - the same URL already in flight is shared instead of sent twice;
 *  - an answer is reused for TTL_MS (going back to the dashboard costs nothing).
 * The server also caches these figures for 2 minutes (DashboardController).
 */
const TTL_MS = 60_000;
const MAX_PARALLEL = 2;

const cache = new Map<string, { at: number; data: any }>();
const inFlight = new Map<string, Promise<any>>();
const waiting: Array<() => void> = [];
let running = 0;

async function inQueue<T>(task: () => Promise<T>): Promise<T> {
  if (running >= MAX_PARALLEL) {
    await new Promise<void>((resolve) => waiting.push(resolve));
  }
  running++;
  try {
    return await task();
  } finally {
    running--;
    waiting.shift()?.();
  }
}

export function dashboardGet<T = any>(url: string, options: { force?: boolean } = {}): Promise<T> {
  const hit = cache.get(url);
  if (!options.force && hit && Date.now() - hit.at < TTL_MS) {
    return Promise.resolve(hit.data);
  }
  const pending = inFlight.get(url);
  if (pending) return pending;

  const request = inQueue(() => api.get(url))
    .then((res) => {
      cache.set(url, { at: Date.now(), data: res.data });
      return res.data;
    })
    .finally(() => inFlight.delete(url));
  inFlight.set(url, request);
  return request;
}

/** "Actualiser" button: forget the answers kept in this tab. */
export function clearDashboardCache() {
  cache.clear();
}
